import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import UpgradeOptionsTabs from "../components/UpgradeOptionsTabs";
import { NavigationContext, UserContext } from "../contexts";
import { getPageTitle } from "../resources/routes";
import { scrollToTop } from "../util";

const LifestyleOptionsScreen = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [slideIndexes, setSlideIndexes] = useState<number[]>([]);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.resetSelectedLifestyleOptions();
    });

    // If there are pre-selected items, select them by default
    for (const category of userContext.lifestyleCategories) {
      const preselected = userContext.lifestyleOptionsWithPromoPricing.filter(
        (item) => item.preselected && item.category_id === category.id
      );
      if (!userContext.selectedUpgrades.get(category.id)) {
        userContext.setSelectedLifestyleOption(category.id, preselected);
      }
    }

    const _slideIndexes = [];
    for (let i = 0; i < userContext.lifestyleCategories.length; i++) {
      let slideIndex = 0;

      // If there is a pre-selected upgrade, we need to have the slider show it
      const existing = userContext.selectedLifestyleOptions.get(
        userContext.lifestyleCategories[i].id
      );
      if (existing && existing.length > 0) {
        const optionsFromCat =
          userContext.lifestyleOptionsWithPromoPricing.filter(
            (item) => item.category_id === userContext.lifestyleCategories[i].id
          );
        for (let x = 0; x < optionsFromCat.length; x++) {
          if (optionsFromCat[x].id === existing[0].id) {
            // The slider moves 2 slides at a time, so we need to make sure it lands on a number divisable by 2
            slideIndex = x;
            if (slideIndex % 2 !== 0) {
              slideIndex = x - 1;
            }
            break;
          }
        }
      }

      _slideIndexes.push(slideIndex);
    }
    setSlideIndexes(_slideIndexes);

    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    // Move through tabs as the user clicks next
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
  }, [tabIndex, userContext.lifestyleCategories]);

  const onChangeSlideIndex = (tabIndex: number, index: number) => {
    const _slideIndexes = [...slideIndexes];
    _slideIndexes[tabIndex] = index;
    setSlideIndexes(_slideIndexes);
  };

  return (
    <div className={"screen screen-upgrade-options"}>
      <div className={"container"}>
        <h2 className="h1">{getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          Put the icing on the cake with a selection of these additional options
          for your new home.
        </p>
      </div>
      <UpgradeOptionsTabs
        categories={userContext.lifestyleCategories}
        upgrades={userContext.lifestyleOptionsWithPromoPricing}
        selectedUpgrades={userContext.selectedLifestyleOptions}
        setSelectedUpgrade={(id, option) =>
          option !== null &&
          userContext.toggleSelectedLifestyleOption(id, option)
        }
        tabIndex={tabIndex}
        onChangeTabIndex={(index) => setTabIndex(index)}
        slideIndexes={slideIndexes}
        onChangeSlideIndex={onChangeSlideIndex}
      />
    </div>
  );
});

export default LifestyleOptionsScreen;
